import React, { useRef, useState } from "react";
import { Button, Input, Popconfirm, Space, Tooltip, Typography } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";
import ChildRow from "./ChildRow.jsx";

const { Text } = Typography;
const TOUCH_HOLD_MS = 450;

function InlineEditableText({ text, onRename, onEditingChange }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(text || "");
  const pressTimer = useRef(null);

  const startEdit = () => {
    setEditing(true);
    onEditingChange?.(true);
    setValue(text || "");
  };
  const clearTimer = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };
  const onTouchStart = () => {
    if (pressTimer.current) return;
    pressTimer.current = setTimeout(() => {
      startEdit();
      clearTimer();
    }, TOUCH_HOLD_MS);
  };
  const save = async () => {
    const ok = await onRename(value);
    if (ok !== false) {
      setEditing(false);
      onEditingChange?.(false);
    }
  };

  if (editing) {
    return (
      <Space.Compact style={{ width: "100%" }}>
        <Input
          autoFocus
          size="middle"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPressEnter={save}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setEditing(false);
              onEditingChange?.(false);
            }
          }}
        />
        <Button type="primary" size="middle" icon={<CheckOutlined />} onClick={save} />
        <Button
          size="middle"
          icon={<CloseOutlined />}
          onClick={() => {
            setEditing(false);
            onEditingChange?.(false);
          }}
        />
      </Space.Compact>
    );
  }

  return (
    <div
      onDoubleClick={startEdit}
      onTouchStart={onTouchStart}
      onTouchEnd={clearTimer}
      onTouchMove={clearTimer}
      onTouchCancel={clearTimer}
      style={{ padding: "2px 0" }}
    >
      <Text style={{ fontSize: 15 }}>{text}</Text>
    </div>
  );
}

function DraftRow({ placeholder, value, setValue, onSave, onCancel }) {
  return (
    <Space.Compact style={{ width: "100%", marginTop: 6 }}>
      <Input
        autoFocus
        size="middle"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onPressEnter={onSave}
      />
      <Button type="primary" size="middle" icon={<CheckOutlined />} onClick={onSave} />
      <Button size="middle" icon={<CloseOutlined />} onClick={onCancel} />
    </Space.Compact>
  );
}

export default function RootRow({
  root,
  expanded,
  toggleExpand,
  childDraftValue,
  setChildDraftValue,
  saveChild,
  onRename,
  onAddChild,
  onDelete,
}) {
  const [showActions, setShowActions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const children = root.children || [];
  const hasRealChildren = children.length > 0;
  const hasDraftChild = childDraftValue !== undefined;
  const hasChildren = hasRealChildren || hasDraftChild;

  // удалять корень можно только когда он пустой (и нет черновика)
  const canDeleteRoot = !hasRealChildren && !hasDraftChild;

  return (
    <div
      style={{
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        padding: 10,
        transition: "all 0.2s",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          paddingTop: 4,
          paddingBottom: 4,
        }}
      >
        <div
          style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}
          onClick={() => setShowActions((v) => !v)}
        >
          {hasChildren ? (
            expanded ? (
              <CaretDownOutlined
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(root._id);
                }}
                style={{ fontSize: 16 }}
              />
            ) : (
              <CaretRightOutlined
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(root._id);
                }}
                style={{ fontSize: 16 }}
              />
            )
          ) : (
            <span style={{ width: 16 }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <InlineEditableText
              text={root.name}
              onRename={(val) => onRename(root._id, val)}
              onEditingChange={setIsEditing}
            />
          </div>
        </div>

        {(showActions || expanded) && !isEditing && (
          <Space size={8}>
            <Tooltip title="Підкатегорія">
              <Button
                type="text"
                icon={<PlusOutlined style={{ fontSize: 20 }} />}
                size="large"
                style={{ width: 40, height: 40 }}
                onClick={() => {
                  onAddChild?.(root._id);
                  if (!expanded) toggleExpand(root._id);
                }}
              />
            </Tooltip>

            {canDeleteRoot && (
              <Popconfirm
                title="Видалити групу?"
                okText="Так"
                cancelText="Ні"
                okButtonProps={{ danger: true, size: "large" }}
                onConfirm={() => onDelete?.(root._id)}
              >
                <Tooltip title="Видалити">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined style={{ fontSize: 20 }} />}
                    size="large"
                    style={{ width: 40, height: 40 }}
                  />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        )}
      </div>

      {expanded && (
        <div
          style={{
            marginLeft: 26,
            marginTop: 6,
            borderLeft: "2px solid #f0f0f0",
            paddingLeft: 8,
          }}
        >
          {childDraftValue !== undefined && (
            <DraftRow
              placeholder="Назва підкатегорії"
              value={childDraftValue || ""}
              setValue={(v) => setChildDraftValue(v)}
              onSave={() => saveChild(root._id)}
              onCancel={() => setChildDraftValue(undefined)}
            />
          )}

          {children.map((child) => (
            <ChildRow
              key={child._id}
              child={child}
              onRename={onRename}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
